import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.emmanuelupegui.com";
const DEFAULT_OG = `${SITE_URL}/og-cover.jpg`;
const SITE_NAME = "Emmanuel Upegui · Fotografía";

/**
 * Reusable SEO component using react-helmet-async.
 * Sets per-page <title>, meta description, canonical, and OG/Twitter tags.
 *
 * Props:
 * - title: full page title (will be wrapped as "title · Emmanuel Upegui")
 * - description: meta description (max ~160 chars recommended)
 * - path: pathname relative to root, e.g. "/sobre-mi" (used for canonical + og:url)
 * - image: absolute URL for OG image (defaults to og-cover.jpg)
 * - type: "website" | "article" | "profile"
 * - keywords: optional comma-separated string
 * - jsonLd: optional structured data object (will be JSON-stringified)
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  keywords,
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME.split(" · ")[0]}`
    : `${SITE_NAME} · Fotógrafo de la cultura`;
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="es_CO" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
